import { useState, useEffect } from 'react';
import { PageHeader, Badge, cx, type ToneType } from './shared';
import { Icon } from './ui/icons';

interface Framework {
  id: string;
  name: string;
  shortName: string;
  version: string;
  color: string;
  description: string;
  requirements: Requirement[];
}

interface Requirement {
  id: string;
  name: string;
  category: string;
  controls: string[];
}

interface Control {
  id: string;
  name: string;
  status: 'draft' | 'published' | 'changed';
  pass: number;
  fail: number;
  pending: number;
}

const mockFrameworks: Framework[] = [
  {
    id: 'soc2-ii',
    name: 'SOC 2 Type II',
    shortName: 'SOC 2',
    version: 'TSC 2022',
    color: '#0A69DA',
    description: 'Trust Services Criteria — Security, Availability, Confidentiality, Processing Integrity, Privacy.',
    requirements: [
      { id: 'CC6.1', name: 'Logical & physical access controls', category: 'Common Criteria', controls: ['AUTH01', 'RBAC01'] },
      { id: 'CC6.6', name: 'Encryption at rest and in transit', category: 'Common Criteria', controls: ['EDR01'] },
      { id: 'CC6.7', name: 'Restrict transmission of information', category: 'Common Criteria', controls: ['DAZ01'] },
      { id: 'CC7.1', name: 'Detection of configuration changes', category: 'Common Criteria', controls: ['LOG01'] },
      { id: 'CC7.2', name: 'Monitoring of system anomalies', category: 'Common Criteria', controls: ['LOG01'] },
      { id: 'CC8.1', name: 'Vulnerability management', category: 'Common Criteria', controls: ['DEP01', 'SEC01'] },
      { id: 'A1.2', name: 'Recoverability — backups verified', category: 'Availability', controls: ['BKP01'] }
    ]
  },
  {
    id: 'gdpr',
    name: 'GDPR',
    shortName: 'GDPR',
    version: 'Regulation (EU) 2016/679',
    color: '#7F56D9',
    description: 'EU General Data Protection Regulation — protecting natural persons regarding personal data processing.',
    requirements: [
      { id: 'Art. 5(1)(c)', name: 'Data minimization', category: 'Principles', controls: ['DAZ01'] },
      { id: 'Art. 5(1)(e)', name: 'Storage limitation', category: 'Principles', controls: ['PGE01'] },
      { id: 'Art. 7', name: 'Conditions for consent', category: 'Lawfulness', controls: ['CM01'] },
      { id: 'Art. 17', name: 'Right to erasure', category: 'Rights', controls: ['PGE01'] },
      { id: 'Art. 32(1)(a)', name: 'Pseudonymization and encryption', category: 'Security', controls: ['EDR01', 'DAZ01'] },
      { id: 'Art. 32(1)(b)', name: 'Ongoing confidentiality & integrity', category: 'Security', controls: ['AUTH01', 'RBAC01'] },
      { id: 'Art. 32(1)(d)', name: 'Regular testing of measures', category: 'Security', controls: ['DEP01'] }
    ]
  },
  {
    id: 'iso-27001',
    name: 'ISO/IEC 27001',
    shortName: 'ISO 27001',
    version: '2022',
    color: '#159D4C',
    description: 'Information Security Management System — Annex A controls (93 controls in 4 themes).',
    requirements: [
      { id: 'A.5.15', name: 'Access control', category: 'Organizational', controls: ['AUTH01', 'RBAC01'] },
      { id: 'A.8.10', name: 'Information deletion', category: 'Technological', controls: ['PGE01'] },
      { id: 'A.8.11', name: 'Data masking', category: 'Technological', controls: ['DAZ01'] },
      { id: 'A.8.24', name: 'Use of cryptography', category: 'Technological', controls: ['EDR01'] },
      { id: 'A.8.8', name: 'Management of technical vulns', category: 'Technological', controls: ['DEP01'] },
      { id: 'A.8.15', name: 'Logging', category: 'Technological', controls: ['LOG01'] },
      { id: 'A.8.13', name: 'Information backup', category: 'Technological', controls: ['BKP01'] }
    ]
  },
  {
    id: 'pci-dss',
    name: 'PCI DSS',
    shortName: 'PCI DSS',
    version: 'v4.0',
    color: '#FA500E',
    description: 'Payment Card Industry Data Security Standard — 12 requirements for cardholder data environments.',
    requirements: [
      { id: 'Req. 3', name: 'Protect stored account data', category: 'Protect', controls: ['EDR01'] },
      { id: 'Req. 6', name: 'Develop and maintain secure systems', category: 'Maintain', controls: ['DEP01', 'SEC01'] },
      { id: 'Req. 7', name: 'Restrict access by business need', category: 'Access', controls: ['RBAC01'] },
      { id: 'Req. 8', name: 'Identify users and authenticate access', category: 'Access', controls: ['AUTH01'] },
      { id: 'Req. 10', name: 'Log and monitor all access', category: 'Monitor', controls: ['LOG01'] }
    ]
  }
];

const mockControls: Control[] = [
  { id: 'AUTH01', name: 'Multi-factor Authentication', status: 'published', pass: 42, fail: 2, pending: 1 },
  { id: 'RBAC01', name: 'Role-based Access Control', status: 'published', pass: 38, fail: 0, pending: 0 },
  { id: 'EDR01', name: 'Encryption Data at Rest', status: 'published', pass: 35, fail: 3, pending: 0 },
  { id: 'DAZ01', name: 'Data Anonymization', status: 'draft', pass: 0, fail: 0, pending: 5 },
  { id: 'LOG01', name: 'Audit Logging', status: 'published', pass: 28, fail: 1, pending: 2 },
  { id: 'DEP01', name: 'Dependency Scanning', status: 'published', pass: 45, fail: 8, pending: 0 },
  { id: 'SEC01', name: 'Security Testing', status: 'changed', pass: 33, fail: 5, pending: 3 },
  { id: 'BKP01', name: 'Backup Verification', status: 'published', pass: 12, fail: 0, pending: 1 },
  { id: 'PGE01', name: 'Data Purge', status: 'draft', pass: 0, fail: 0, pending: 3 },
  { id: 'CM01', name: 'Consent Management', status: 'draft', pass: 0, fail: 0, pending: 2 }
];

function calculateCoverage(framework: Framework, controls: Control[]) {
  let satisfied = 0;
  let partial = 0;
  let unmapped = 0;

  framework.requirements.forEach(req => {
    if (req.controls.length === 0) {
      unmapped++;
      return;
    }
    
    const ctrls = req.controls.map(id => controls.find(c => c.id === id)).filter(Boolean) as Control[];
    if (!ctrls.length) {
      unmapped++;
      return;
    }

    const allPass = ctrls.every(c => {
      if (c.status !== 'published') return false;
      const runs = c.pass + c.fail + c.pending;
      if (runs === 0) return false;
      return c.pass / runs >= 0.85;
    });

    const anyDraftOrFailing = ctrls.some(c => c.status === 'draft' || c.fail > 0 || c.status === 'changed');
    
    if (allPass) satisfied++;
    else if (anyDraftOrFailing) partial++;
    else unmapped++;
  });

  const total = framework.requirements.length;
  return {
    satisfied,
    partial,
    unmapped,
    total,
    pct: total ? Math.round((satisfied / total) * 100) : 0,
  };
}

interface CoverageRingProps {
  pct: number;
  color: string;
  size?: number;
  thickness?: number;
}

function CoverageRing({ pct, color, size = 88, thickness = 10 }: CoverageRingProps) {
  const r = (size - thickness) / 2;
  const C = 2 * Math.PI * r;
  const dash = (pct / 100) * C;
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`translate(${size/2}, ${size/2}) rotate(-90)`}>
        <circle r={r} fill="none" stroke="#F7EDDB" strokeWidth={thickness} />
        <circle r={r} fill="none" stroke={color} strokeWidth={thickness} strokeDasharray={`${dash} ${C - dash}`} strokeLinecap="round" />
      </g>
      <text x={size/2} y={size/2 + 1} textAnchor="middle" dominantBaseline="middle"
            fontFamily="Raleway" fontWeight="700" fontSize={size * 0.32} fill="#121111">
        {pct}<tspan fontSize={size * 0.18} fill="#6B7280">%</tspan>
      </text>
    </svg>
  );
}

interface CoverBlockProps {
  label: string;
  value: number;
  tone: ToneType;
}

function CoverBlock({ label, value, tone }: CoverBlockProps) {
  return (
    <div className="rounded-[8px] border border-[#F7EDDB] px-3 py-2">
      <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">{label}</div>
      <div className="font-display font-bold mt-0.5 text-[22px]" style={{ color: tone === 'ok' ? '#159D4C' : tone === 'warn' ? '#CA9401' : '#6B7280' }}>
        {value}
      </div>
    </div>
  );
}

export default function ComplianceFrameworks() {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [controls, setControls] = useState<Control[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setFrameworks(mockFrameworks);
      setControls(mockControls);
      setLoading(false);
    };
    
    loadData();
  }, []);

  const selectedFrameworkData = selectedFramework ? frameworks.find(f => f.id === selectedFramework) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading compliance frameworks...</span>
        </div>
      </div>
    );
  }

  if (selectedFrameworkData) {
    const coverage = calculateCoverage(selectedFrameworkData, controls);
    const groups = [...new Set(selectedFrameworkData.requirements.map(r => r.category))];

    const getRequirementStatus = (req: Requirement) => {
      const ctrls = req.controls.map(id => controls.find(c => c.id === id)).filter(Boolean) as Control[];
      if (!ctrls.length) return 'unmapped';
      
      const allPass = ctrls.every(c => {
        if (c.status !== 'published') return false;
        const runs = c.pass + c.fail + c.pending;
        return runs > 0 && c.pass / runs >= 0.85;
      });
      
      if (allPass) return 'satisfied';
      const someProblem = ctrls.some(c => c.status === 'draft' || c.fail > 0 || c.status === 'changed');
      if (someProblem) return 'partial';
      return 'unmapped';
    };

    const statusBadge = {
      satisfied: <Badge tone="ok" dot>Satisfied</Badge>,
      partial: <Badge tone="warn" dot>Partial</Badge>,
      unmapped: <Badge tone="muted" dot>Unmapped</Badge>,
    };

    return (
      <div className="flex flex-col gap-8">
        <div className="flex items-center gap-2 text-[14px]">
          <button
            onClick={() => setSelectedFramework(null)}
            className="text-primary-500 hover:text-primary-600 font-display"
          >
            Frameworks
          </button>
          <Icon name="chevron-right" size={12} className="text-ink/40" />
          <span className="text-ink/60 font-display">{selectedFrameworkData.name}</span>
        </div>

        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="h-11 w-11 rounded-[10px] grid place-items-center" 
                style={{ 
                  background: `${selectedFrameworkData.color}1a`, 
                  border: `1px solid ${selectedFrameworkData.color}33` 
                }}
              >
                <Icon name="shield" size={20} stroke={selectedFrameworkData.color} />
              </div>
              <div>
                <h1 className="font-display font-bold text-ink leading-none text-[32px]">{selectedFrameworkData.name}</h1>
                <div className="font-data text-[12px] text-ink/55 mt-2">{selectedFrameworkData.version}</div>
              </div>
            </div>
            <p className="font-display text-[15px] text-ink/75 max-w-[820px] mt-3">{selectedFrameworkData.description}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40">
              <Icon name="download" size={14} stroke="#121111" /> Export evidence
            </button>
            <button className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[15px] inline-flex items-center gap-2 hover:opacity-90 whitespace-nowrap shrink-0">
              <Icon name="plus" size={14} stroke="white" /> Map a control
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5 flex items-center gap-4">
            <CoverageRing pct={coverage.pct} color={selectedFrameworkData.color} size={72} thickness={8} />
            <div>
              <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Coverage</div>
              <div className="font-display font-bold text-ink mt-0.5 text-[24px]">{coverage.pct}%</div>
            </div>
          </div>
          <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
            <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Requirements</div>
            <div className="font-display font-bold text-ink mt-0.5 text-[24px]">{coverage.total}</div>
          </div>
          <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
            <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Satisfied</div>
            <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#159D4C' }}>{coverage.satisfied}</div>
          </div>
          <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
            <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Gaps</div>
            <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: coverage.partial + coverage.unmapped ? '#CA9401' : '#6B7280' }}>
              {coverage.partial + coverage.unmapped}
            </div>
          </div>
        </div>

        {groups.map(group => {
          const reqs = selectedFrameworkData.requirements.filter(r => r.category === group);
          return (
            <div key={group} className="rounded-[10px] border border-[#F3E4C8] bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[11px] uppercase tracking-[0.1em] text-ink/60">
                {group}
              </div>
              <div className="grid items-center px-5 h-9 border-b border-[#F7EDDB] font-display text-[10px] font-bold uppercase tracking-[0.04em] text-ink/45"
                   style={{ gridTemplateColumns: "120px 1.4fr 1.6fr 130px" }}>
                <span>Clause</span><span>Requirement</span><span>Controls</span><span>Status</span>
              </div>
              <ul>
                {reqs.map(req => {
                  const ctrls = req.controls.map(id => controls.find(c => c.id === id)).filter(Boolean) as Control[];
                  const status = getRequirementStatus(req);
                  return (
                    <li key={req.id} className="grid items-center px-5 py-4 border-t border-[#F7EDDB] first:border-0 hover:bg-[#FCF8F1]"
                        style={{ gridTemplateColumns: "120px 1.4fr 1.6fr 130px" }}>
                      <span className="font-data text-[12px] text-ink/70">{req.id}</span>
                      <span className="font-display font-bold text-ink text-[14px]">{req.name}</span>
                      <div className="flex flex-wrap gap-1.5">
                        {ctrls.length ? ctrls.map(c => (
                          <div
                            key={c.id}
                            className="inline-flex items-center gap-1.5 h-7 px-2 rounded-[6px] border border-[#F3E4C8] hover:border-ink/40"
                          >
                            <span className="font-data text-[11px] text-ink/55">#{c.id}</span>
                            <span className="font-display font-semibold text-ink text-[12px] truncate max-w-[200px]">{c.name}</span>
                            {c.fail > 0 ? <span className="h-1.5 w-1.5 rounded-full bg-[#F40B0B]" /> :
                             c.status === 'draft' ? <span className="h-1.5 w-1.5 rounded-full bg-[#CA9401]" /> :
                             <span className="h-1.5 w-1.5 rounded-full bg-[#159D4C]" />}
                          </div>
                        )) : <span className="font-data text-[12px] text-ink/40">No control mapped</span>}
                      </div>
                      {statusBadge[status as keyof typeof statusBadge]}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Compliance Frameworks"
        subtitle="Map your controls to the standards your customers and auditors ask about."
        right={
          <button className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40 shrink-0">
            <Icon name="plus" size={14} stroke="#121111" /> Add framework
          </button>
        }
      />

      <div className="grid grid-cols-2 gap-5">
        {frameworks.map(f => {
          const coverage = calculateCoverage(f, controls);
          return (
            <button
              key={f.id}
              onClick={() => setSelectedFramework(f.id)}
              className="text-left rounded-[10px] border border-[#F3E4C8] bg-white p-6 hover:border-ink/40 transition flex gap-6"
            >
              <CoverageRing pct={coverage.pct} color={f.color} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-ink text-[18px]">{f.name}</span>
                  <span className="font-data text-[11px] text-ink/45">{f.version}</span>
                </div>
                <p className="font-display text-[13px] text-ink/65 mt-1 line-clamp-2">{f.description}</p>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <CoverBlock label="Satisfied" value={coverage.satisfied} tone="ok" />
                  <CoverBlock label="Partial" value={coverage.partial} tone="warn" />
                  <CoverBlock label="Unmapped" value={coverage.unmapped} tone="muted" />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}